import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Bot, Zap, ShieldCheck, ChevronRight, Link2, MessageSquare, Send,
  Hotel, Stethoscope, Utensils, Dumbbell, Scissors, Building2,
  CheckCircle2, ArrowRight, BarChart3, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/AppLayout";

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } } };

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="landing-page">
      <Header />
      <Hero />
      <SocialProof />
      <Features />
      <HowItWorks />
      <BusinessTemplates />
      <Pricing />
      <Testimonials />
      <CTA />
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 glass" data-testid="landing-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-12 h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="logo">
          <Logo />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#how" className="hover:text-white transition-colors">How it works</a>
          <a href="#templates" className="hover:text-white transition-colors">Templates</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-zinc-300 hover:text-white" data-testid="nav-login-link">Sign in</Link>
          <Link to="/signup" data-testid="nav-signup-link">
            <Button className="bg-white text-black hover:bg-zinc-200 rounded-md px-4 h-9 text-sm font-medium">
              Start Free <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-white/10" data-testid="hero-section">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(16,185,129,0.45), transparent 60%)" }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32">
        <motion.div initial="initial" animate="animate" variants={stagger} className="max-w-4xl">
          <motion.div variants={fadeUp}>
            <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/15 rounded-full px-3 py-1 mb-6" data-testid="hero-badge">
              <Sparkles className="w-3 h-3 mr-1" /> One prompt → Your AI inquiry agent
            </Badge>
          </motion.div>
          <motion.h1 variants={fadeUp} className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-medium text-balance" data-testid="hero-headline">
            Capture inquiries on{" "}
            <span className="relative inline-block">
              <span className="text-emerald-400">autopilot</span>
              <span className="absolute -inset-1 bg-emerald-500/15 blur-xl -z-10" />
            </span>{" "}
            with your own AI agent.
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-6 text-zinc-400 text-lg sm:text-xl max-w-2xl leading-relaxed" data-testid="hero-sub">
            Describe your business in one prompt. Get a shareable link. Your AI chats with customers, answers FAQs, and drops every lead into your admin panel — 24/7.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="cta-start-trial">
              <Button className="bg-white text-black hover:bg-zinc-200 rounded-md h-12 px-6 text-base font-medium">
                Create your AI agent <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="#how" data-testid="cta-how">
              <Button variant="outline" className="bg-transparent text-white border-white/20 hover:bg-white/10 rounded-md h-12 px-6 text-base">
                See how it works
              </Button>
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8 flex items-center gap-6 text-xs text-zinc-500 tracking-wider uppercase flex-wrap">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> 14-day free trial</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> No credit card</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Live in 60 seconds</span>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="mt-20">
          <HeroDemo />
        </motion.div>
      </div>
    </section>
  );
}

function HeroDemo() {
  return (
    <div className="ai-border rounded-2xl bg-zinc-950 p-1" data-testid="hero-demo">
      <div className="rounded-xl bg-black/60 grid lg:grid-cols-[1fr_1fr_1fr] overflow-hidden">
        <div className="p-7 border-b lg:border-b-0 lg:border-r border-white/5">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">1. You type</div>
          <div className="font-mono text-sm text-zinc-300 bg-zinc-950/80 border border-white/10 rounded-lg p-4">
            <span className="text-emerald-400">$ </span>"Hotel booking agent. Deluxe ₹3,500. Suite ₹5,500. Ask check-in date and party size."
          </div>
          <div className="mt-5 inline-flex items-center gap-2 text-xs px-3 py-1.5 bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> AI generated
          </div>
        </div>

        <div className="p-7 border-b lg:border-b-0 lg:border-r border-white/5 bg-zinc-950/40">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">2. Share link</div>
          <div className="bg-black border border-white/10 rounded-lg p-3 font-mono text-xs text-zinc-300 flex items-center gap-2">
            <Link2 className="w-3.5 h-3.5 text-emerald-400 shrink-0"/>
            <span className="truncate">ipnovaai/c/hyt8z2k1</span>
          </div>
          <div className="mt-3 text-xs text-zinc-500">Share via Instagram bio, Google profile, ads — anywhere.</div>
          <div className="mt-5 grid grid-cols-3 gap-1.5">
            {["Instagram","Google","Ads","WhatsApp","Email","SMS"].map(n => (
              <div key={n} className="text-[10px] text-center px-2 py-1.5 bg-white/[0.03] border border-white/10 rounded text-zinc-400">{n}</div>
            ))}
          </div>
        </div>

        <div className="p-7 bg-zinc-950/60">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">3. AI chats live</div>
          <div className="space-y-2">
            <Bubble side="left" ai>Hi! Welcome to Ragavi Hotels. How can I help with your stay?</Bubble>
            <Bubble side="right">Rooms for the weekend?</Bubble>
            <Bubble side="left" ai>Yes! Deluxe ₹3,500/night, Suite ₹5,500/night. Check-in date and how many guests?</Bubble>
            <Bubble side="right">Friday, 2 guests, 2 nights</Bubble>
            <Bubble side="left" ai>Perfect — that's ₹7,000 total. Could I get your name and phone?</Bubble>
          </div>
          <div className="mt-3 text-[11px] text-emerald-400 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3"/> Lead captured to admin panel
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ children, side = "left", ai }) {
  const isLeft = side === "left";
  return (
    <div className={`flex ${isLeft ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[85%] ${isLeft ? "bg-zinc-900 border-white/10" : "bg-emerald-500/15 border-emerald-500/25"} border rounded-2xl px-3 py-2`}>
        <div className="text-[13px] text-zinc-100 leading-relaxed">{children}</div>
        {ai && <div className="mt-0.5 text-[10px] text-emerald-400/80 flex items-center gap-1"><Bot className="w-3 h-3" /> AI</div>}
      </div>
    </div>
  );
}

function SocialProof() {
  return (
    <section className="border-b border-white/10 py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-8 text-center">Trusted by businesses who hate missed inquiries</div>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-60">
          {["RAGAVI HOTELS", "PULSE CLINIC", "TANDOOR HOUSE", "FLEX GYM", "BLOOM SALON", "EVERLAND REALTY"].map((n) => (
            <div key={n} className="font-display tracking-[0.2em] text-zinc-500 text-sm">{n}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { icon: Bot, title: "Prompt → AI Agent", desc: "Type plain English. We build a conversational agent with your tone, knowledge and questions." },
    { icon: Link2, title: "Shareable link, not WhatsApp", desc: "Each agent gets a public chat URL. Drop it in Instagram bio, Google, ads — done." },
    { icon: MessageSquare, title: "Smart conversations", desc: "AI greets, answers FAQs, qualifies, and asks for the info you need — one question at a time." },
    { icon: Zap, title: "Auto lead capture", desc: "Every completed chat lands as a structured lead in your admin panel with all details." },
    { icon: BarChart3, title: "Real-time analytics", desc: "Chats started, leads captured, conversion rate. Know what's working." },
    { icon: ShieldCheck, title: "Your brand, your control", desc: "Pause, edit, regenerate any agent in one click. No code required." },
  ];
  return (
    <section id="features" className="py-24 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Features</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">An AI inquiry agent that never sleeps.</h2>
          <p className="mt-4 text-zinc-400">Stop missing leads at 2 AM. ipnova's AI handles every customer who taps your link.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {items.map((it, i) => (
            <motion.div key={it.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-black p-8 hover:bg-zinc-950 transition-colors group">
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 grid place-items-center mb-5 group-hover:bg-emerald-500/10 group-hover:border-emerald-500/30 transition-colors">
                <it.icon className="w-5 h-5 text-zinc-300 group-hover:text-emerald-400" />
              </div>
              <h3 className="font-display text-xl tracking-tight mb-2">{it.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Describe your business", desc: "One paragraph: what you sell, prices, hours, what to ask customers." },
    { n: "02", title: "AI builds your agent", desc: "Persona, knowledge, FAQs, capture fields — all generated in seconds." },
    { n: "03", title: "Get your share link", desc: "Each agent gets a unique ipnovaai/c/xxxx link. Share it anywhere." },
    { n: "04", title: "Leads in admin panel", desc: "Customers chat. AI captures details. You follow up from your dashboard." },
  ];
  return (
    <section id="how" className="py-24 border-b border-white/10 dot-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Workflow</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">Idea to live agent in 60 seconds.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="relative bg-zinc-950/80 border border-white/10 rounded-xl p-6 hover:-translate-y-1 transition-transform">
              <div className="font-mono text-sm text-emerald-400 mb-4">{s.n}</div>
              <div className="font-display text-lg tracking-tight mb-2">{s.title}</div>
              <div className="text-sm text-zinc-400">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BusinessTemplates() {
  const items = [
    { icon: Hotel, title: "Hotels", desc: "Bookings, rates, availability" },
    { icon: Stethoscope, title: "Clinics", desc: "Appointments, reminders" },
    { icon: Utensils, title: "Restaurants", desc: "Menu, reservations" },
    { icon: Dumbbell, title: "Gyms", desc: "Memberships, free trials" },
    { icon: Scissors, title: "Salons", desc: "Bookings, services" },
    { icon: Building2, title: "Real Estate", desc: "Site visits, qualification" },
  ];
  return (
    <section id="templates" className="py-24 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-12">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Prebuilt agents</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">Ready-to-use AI agents for every business.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((it) => (
            <div key={it.title} className="bg-zinc-950 border border-white/10 rounded-xl p-5 hover:border-emerald-500/30 hover:bg-emerald-500/[0.02] transition-all">
              <it.icon className="w-5 h-5 text-emerald-400 mb-3" />
              <div className="font-display text-base tracking-tight">{it.title}</div>
              <div className="text-xs text-zinc-500 mt-1">{it.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="py-24 border-b border-white/10" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-12">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Pricing</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">One simple plan. Two billing cycles.</h2>
          <p className="mt-3 text-zinc-400">14-day free trial. Then choose monthly or yearly. Cancel anytime.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
          <PricingCard name="Monthly" price="₹1,500" period="/month" features={["Unlimited AI agents", "Unlimited chats", "Lead capture & CRM", "Live analytics", "Email support"]} />
          <PricingCard name="Yearly" price="₹12,000" period="/year" highlighted savings="Save ₹6,000" features={["Everything in Monthly", "33% discount", "Priority support", "White-label option", "Team workspaces"]} />
        </div>
        <div className="mt-6 text-sm text-zinc-400 max-w-4xl">
          <span className="font-mono text-emerald-400">PROMO IP3725</span> — extra 25% off your first billing cycle.
        </div>
      </div>
    </section>
  );
}

function PricingCard({ name, price, period, features, highlighted, savings }) {
  return (
    <div className={`relative rounded-2xl p-8 border transition-all ${highlighted ? "bg-zinc-950 border-emerald-500/30 ai-glow" : "bg-zinc-950 border-white/10"}`} data-testid={`pricing-${name.toLowerCase()}`}>
      {highlighted && (<div className="absolute -top-3 left-8 text-[10px] tracking-[0.2em] uppercase bg-emerald-500 text-black font-medium rounded-full px-3 py-1">Most Popular</div>)}
      <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">{name}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-5xl tracking-tighter font-medium">{price}</span>
        <span className="text-zinc-500">{period}</span>
      </div>
      {savings && <div className="mt-1 text-xs text-emerald-400">{savings}</div>}
      <ul className="mt-6 space-y-3">
        {features.map((f) => (<li key={f} className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {f}</li>))}
      </ul>
      <Link to="/signup" className="block mt-8" data-testid={`pricing-cta-${name.toLowerCase()}`}>
        <Button className={highlighted ? "w-full bg-white text-black hover:bg-zinc-200 h-11" : "w-full bg-transparent border border-white/20 hover:bg-white/10 text-white h-11"}>
          Start free trial <ChevronRight className="ml-1 w-4 h-4" />
        </Button>
      </Link>
    </div>
  );
}

function Testimonials() {
  const t = [
    { name: "Anika Mehta", title: "Owner, Bloom Salon", quote: "I dropped my ipnova link in my Instagram bio. Bookings went up 4x in 2 weeks." },
    { name: "Rohan Kapoor", title: "Manager, Pulse Clinic", quote: "Patients book appointments at 2 AM and we wake up to clean leads. Felt like magic." },
    { name: "Vikram Shah", title: "GM, Ragavi Hotels", quote: "Inquiry-to-booking conversion went from 22% to 61%. Pays for itself in a week." },
  ];
  return (
    <section className="py-24 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-12">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Customers</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">Loved by founders & operators.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {t.map((x) => (
            <div key={x.name} className="bg-zinc-950 border border-white/10 rounded-2xl p-7 hover:border-white/20 transition">
              <div className="text-zinc-200 leading-relaxed">"{x.quote}"</div>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500/40 to-emerald-500/10 border border-emerald-500/30 grid place-items-center font-display">{x.name[0]}</div>
                <div>
                  <div className="text-sm font-medium">{x.name}</div>
                  <div className="text-xs text-zinc-500">{x.title}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-24 relative overflow-hidden border-b border-white/10">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(16,185,129,0.5), transparent 60%)" }} />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <h2 className="font-display text-4xl sm:text-6xl tracking-tighter font-medium text-balance">Your AI inquiry agent is one prompt away.</h2>
        <p className="mt-6 text-zinc-400 text-lg">Describe. Share link. Watch leads roll in.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/signup">
            <Button className="bg-white text-black hover:bg-zinc-200 h-12 px-7 text-base">Start free trial <ArrowRight className="ml-2 w-4 h-4" /></Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 h-12 px-7 text-base">Sign in</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-8 items-start">
        <div>
          <div className="flex items-center gap-2"><Logo /></div>
          <div className="text-zinc-500 text-sm mt-3 max-w-xs">Capture inquiries on autopilot with your own AI agent. One prompt away.</div>
          <div className="text-zinc-600 text-xs mt-4">© 2026 ipnova AI. All rights reserved.</div>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Contact</div>
          <ul className="space-y-2 text-sm">
            <li><a href="mailto:tech@ipnovaai.com" className="text-zinc-300 hover:text-white inline-flex items-center gap-2" data-testid="footer-email">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/> tech@ipnovaai.com
            </a></li>
            <li><a href="https://instagram.com/ipnova.ai" target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white inline-flex items-center gap-2" data-testid="footer-instagram">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/> @ipnova.ai
            </a></li>
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Team</div>
          <div className="text-sm text-zinc-300" data-testid="footer-founder">
            <div className="font-display text-lg tracking-tight">Parth Tiwari</div>
            <div className="text-xs text-zinc-500 mt-0.5">Founder</div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
        <div className="text-zinc-600 text-xs">Made with care in India · Powered by AI</div>
        <div className="flex items-center gap-6 text-sm text-zinc-500">
          <a href="#" className="hover:text-white">Privacy</a>
          <a href="#" className="hover:text-white">Terms</a>
          <a href="mailto:tech@ipnovaai.com" className="hover:text-white">Support</a>
        </div>
      </div>
    </footer>
  );
}
